// Incoming-message chime.
//
// Synthesised with the Web Audio API rather than shipping an audio file: no
// extra asset to load or copy between the two apps, and it can't 404. It's a
// soft two-note rise (E6 -> A6) with a quick decay - noticeable in an office
// without being a doorbell.
//
// Browsers block audio until the user has interacted with the page. That's
// fine here: chat is only used after clicking around, and every failure is
// swallowed so a blocked sound can never break message delivery.

const MUTE_KEY = 'sf.chatMuted';

let context: AudioContext | null = null;

function getContext(): AudioContext | null {
  try {
    const Ctor = window.AudioContext || (window as any).webkitAudioContext;
    if (!Ctor) return null;
    if (!context) context = new Ctor();
    // Autoplay policy parks the context until a gesture unlocks it.
    if (context.state === 'suspended') void context.resume();
    return context;
  } catch {
    return null;
  }
}

export function isChatMuted(): boolean {
  try {
    return localStorage.getItem(MUTE_KEY) === 'true';
  } catch {
    return false;
  }
}

export function setChatMuted(muted: boolean): void {
  try {
    localStorage.setItem(MUTE_KEY, String(muted));
  } catch {
    /* storage unavailable - preference just won't persist */
  }
}

/** Two-note chime. `emphasis` gives mentions a slightly brighter, longer ring. */
export function playMessageChime(emphasis = false): void {
  if (isChatMuted()) return;
  const ctx = getContext();
  if (!ctx) return;

  try {
    const now = ctx.currentTime;
    const notes = emphasis ? [1318.5, 1760.0] : [1046.5, 1318.5]; // E6->A6 : C6->E6
    const gain = ctx.createGain();
    gain.connect(ctx.destination);
    // Keep it quiet; this fires while people are working.
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(emphasis ? 0.09 : 0.06, now + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + (emphasis ? 0.45 : 0.32));

    notes.forEach((frequency, index) => {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(frequency, now + index * 0.09);
      osc.connect(gain);
      osc.start(now + index * 0.09);
      osc.stop(now + 0.5);
    });
  } catch {
    /* never let audio break the chat */
  }
}
